import { useCallback } from 'react';
import { ApolloError, useApolloClient, useMutation } from '@apollo/client';

import {
  PhoneVerificationStatusDocument,
  SendPhoneVerificationCodeDocument,
  VerifyPhoneVerificationCodeDocument,
  ChangePhoneVerificationNumberDocument,
} from 'src/gql/graphql';
import type {
  PhoneVerificationStatusQuery,
  SendPhoneVerificationCodeMutation,
  VerifyPhoneVerificationCodeMutation,
  ChangePhoneVerificationNumberMutation,
} from 'src/gql/graphql';
import { getLogger } from 'src/utils/datadog-logger';

const logger = getLogger('phone-verification-nudge');

/**
 * Normalized shape for errors thrown by any of the four phone-verification
 * operations. Under the FINTECH-4689 backend rewrite, every user-actionable
 * outcome (rate limits, invalid/undeliverable numbers, etc.) is a 2xx response
 * carrying `errorCode` -- it never throws. What reaches here is a genuine
 * system fault: a 5xx (`server_error`), a connection failure that never got an
 * HTTP response at all (`network_error`), or anything else (`unknown`).
 */
export type NormalizedPhoneVerificationError = {
  kind: 'network_error' | 'server_error' | 'unknown';
  status?: number;
};

/**
 * Best-effort extraction of an HTTP status code from an Apollo error. Checks,
 * in order:
 *  - `networkError.statusCode` -- set when the whole HTTP response is
 *    non-2xx (see `src/components/load-performance-mode/load-performance-mode.tsx`
 *    for existing precedent reading this field).
 *  - `graphQLErrors[].extensions.response.status` / `.statusCode` -- a shape
 *    some REST-directive-backed resolvers attach to the extensions of a
 *    resolver-thrown GraphQLError.
 *  - `graphQLErrors[].extensions.code`, when it happens to be a numeric-like
 *    string.
 *
 * Only used to detect a genuine 5xx (`normalizePhoneVerificationError`'s
 * `server_error`) -- user-actionable outcomes no longer throw at all under the
 * FINTECH-4689 contract, so finer-grained status codes aren't meaningful here.
 */
function extractHttpStatus(error: unknown): number | undefined {
  if (!(error instanceof ApolloError)) {
    return undefined;
  }

  const networkStatus = (error.networkError as { statusCode?: number } | null)?.statusCode;
  if (typeof networkStatus === 'number') {
    return networkStatus;
  }

  const statusFromGraphQLErrors = error.graphQLErrors
    .map((graphQLError): number | undefined => {
      const extensions = graphQLError.extensions as
        | { code?: number | string; response?: { status?: number; statusCode?: number } }
        | undefined;

      const responseStatus = extensions?.response?.status ?? extensions?.response?.statusCode;
      if (typeof responseStatus === 'number') {
        return responseStatus;
      }

      if (extensions?.code === undefined || extensions.code === '') {
        return undefined;
      }

      const codeAsNumber = Number(extensions.code);
      return Number.isNaN(codeAsNumber) ? undefined : codeAsNumber;
    })
    .find((status): status is number => status !== undefined);

  return statusFromGraphQLErrors;
}

/**
 * Normalizes any error thrown by the phone-verification mutations/query into
 * a `{ kind, status? }` shape callers can branch on without re-deriving the
 * HTTP status from Apollo internals each time. `status` is kept (rather than
 * dropped along with `throttled`/`invalid_request`) because the `logger.error`
 * call sites below still spread it into their observability payload.
 */
export function normalizePhoneVerificationError(error: unknown): NormalizedPhoneVerificationError {
  const status = extractHttpStatus(error);

  if (status !== undefined && status >= 500) {
    return { kind: 'server_error', status };
  }

  if (error instanceof ApolloError && error.networkError && status === undefined) {
    return { kind: 'network_error' };
  }

  return { kind: 'unknown', status };
}

export type UsePhoneVerificationResult = {
  fetchStatus: (dispensaryId?: string | null) => Promise<PhoneVerificationStatusQuery>;
  sendCode: (dispensaryId?: string | null) => Promise<SendPhoneVerificationCodeMutation>;
  verifyCode: (code: string, dispensaryId?: string | null) => Promise<VerifyPhoneVerificationCodeMutation>;
  changeNumber: (phone: string, dispensaryId?: string | null) => Promise<ChangePhoneVerificationNumberMutation>;
};

/**
 * Thin Apollo hook wrapping the four phone-verification operations. Exposes
 * `sendCode` / `verifyCode` / `changeNumber` for the real flow (the modal
 * receives its initial status as `openModal` data, per the PLAN -- see
 * `fetchStatus` below), plus a standalone `fetchStatus` for callers that need
 * to check the tri-state status directly.
 */
export function usePhoneVerification(): UsePhoneVerificationResult {
  // The real flow receives the tri-state status as `openModal` data, computed once by the
  // caller before the modal is shown; `fetchStatus` exists for other callers that need it
  // directly. It's still implemented properly (not stubbed) because this tri-state must NEVER
  // be served from the Apollo cache -- a stale `unverified` that is really `verified_other`
  // would show exactly the wrong modal. Armageddon also sets `Cache-Control: no-store` on this
  // endpoint, reinforcing network-only here. Precedent for this
  // `apolloClient.query({ fetchPolicy: 'network-only' })` shape:
  // `src/hooks/use-post-signup-modal-sequence.ts` (`GetRewardsV2Document`).
  const apolloClient = useApolloClient();

  const [sendCodeMutation] = useMutation(SendPhoneVerificationCodeDocument);
  const [verifyCodeMutation] = useMutation(VerifyPhoneVerificationCodeDocument);
  const [changeNumberMutation] = useMutation(ChangePhoneVerificationNumberDocument);

  const fetchStatus = useCallback(
    async (dispensaryId?: string | null): Promise<PhoneVerificationStatusQuery> => {
      try {
        const { data } = await apolloClient.query({
          query: PhoneVerificationStatusDocument,
          variables: { dispensaryId },
          fetchPolicy: 'network-only',
        });
        return data;
      } catch (error) {
        logger.error('phoneVerificationStatus failed', { error, ...normalizePhoneVerificationError(error) });
        throw error;
      }
    },
    [apolloClient]
  );

  const sendCode = useCallback(
    async (dispensaryId?: string | null): Promise<SendPhoneVerificationCodeMutation> => {
      try {
        const { data } = await sendCodeMutation({ variables: { dispensaryId } });
        if (!data) {
          throw new Error('sendPhoneVerificationCode returned no data');
        }
        return data;
      } catch (error) {
        logger.error('sendPhoneVerificationCode failed', { error, ...normalizePhoneVerificationError(error) });
        throw error;
      }
    },
    [sendCodeMutation]
  );

  const verifyCode = useCallback(
    async (code: string, dispensaryId?: string | null): Promise<VerifyPhoneVerificationCodeMutation> => {
      try {
        const { data } = await verifyCodeMutation({ variables: { code, dispensaryId } });
        if (!data) {
          throw new Error('verifyPhoneVerificationCode returned no data');
        }
        return data;
      } catch (error) {
        logger.error('verifyPhoneVerificationCode failed', { error, ...normalizePhoneVerificationError(error) });
        throw error;
      }
    },
    [verifyCodeMutation]
  );

  const changeNumber = useCallback(
    async (phone: string, dispensaryId?: string | null): Promise<ChangePhoneVerificationNumberMutation> => {
      try {
        const { data } = await changeNumberMutation({ variables: { phone, dispensaryId } });
        if (!data) {
          throw new Error('changePhoneVerificationNumber returned no data');
        }
        return data;
      } catch (error) {
        logger.error('changePhoneVerificationNumber failed', { error, ...normalizePhoneVerificationError(error) });
        throw error;
      }
    },
    [changeNumberMutation]
  );

  return { fetchStatus, sendCode, verifyCode, changeNumber };
}
